import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import AdminDashboardV2 from './AdminDashboardV2';
import RidesList from './RidesList';
import DriversList from './DriversList';
import RoutePresets from './RoutePresets';
import AirportStand from './AirportStand';
import AirportMapView from './AirportMapView';
import PassengersList from './PassengersList';
import FleetManagement from './FleetManagement';
import SettingsPage from './SettingsPage';
import DriverShiftsPage from './DriverShiftsPage';
import AnalyticsPage from './AnalyticsPage';
import InvoicesPage from './InvoicesPage';
import FinancePage from './FinancePage';
import DailyClosingPage from './DailyClosingPage';
import BackupsPage from './BackupsPage';
import DriverPerformancePage from './DriverPerformancePage';
import DriverProfilePage from './DriverProfilePage';
import VehicleProfilePage from './VehicleProfilePage';
import AdminRideDetailsPage from './AdminRideDetailsPage';
import RideOperationsPage from './RideOperationsPage';
import AuditLogsPage from './AuditLogsPage';
import PaymentsPage from './PaymentsPage';
import ShiftVerificationPage from './ShiftVerificationPage';
import EditDriverPage from './EditDriverPage';
import CashSettlementPage from './CashSettlementPage';
import PushNotificationsPage from '../../pages/PushNotificationsPage';
import { RevenueChart, RidesChart } from './Charts';
import CreateBookingModal from './CreateBookingModal';

// Import from Dispatcher Panel
import CallLog from '../dispatcher-panel/CallLog';

export const DispatcherDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Load from localStorage
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const handleNavigate = (path) => {
    console.log('Navigating to:', path);
    navigate(path);
    setSidebarOpen(false);
  };

  const handleCreateBooking = () => {
    setShowBookingModal(true);
  };

  const handleToggleSidebarCollapse = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    // Save to localStorage
    localStorage.setItem('sidebarCollapsed', newState.toString());
  };

  // Dashboard Home Component - Now uses AdminDashboardV2 with live stats and dual currency
  const DashboardHome = () => (
    <AdminDashboardV2 />
  );

  // Map Page Component - Now with integrated Airport Stand
  const MapPage = () => (
    <AirportMapView />
  );

  // Rides Page Component
  const RidesPage = () => (
    <div className="w-full">
      <RidesList fullWidth />
    </div>
  );

  // Drivers Page Component
  const DriversPage = () => (
    <div className="w-full">
      <DriversList />
    </div>
  );

  // Reports Page Component
  const ReportsPage = () => (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-6">
        <RevenueChart />
        <RidesChart />
      </div>
    </div>
  );

  const getPageTitle = () => {
    const titles = {
      '/admin': 'Përmbledhje',
      '/admin/map': 'Harta Live',
      '/admin/rides': 'Porositë Aktive',
      '/admin/rides/operations': 'Menaxhimi Udhëtimeve',
      '/admin/drivers': 'Shoferët',
      '/admin/performance': 'Performanca e Shoferëve',
      '/admin/shift-verification': 'Verifikimi i Turneve',
      '/admin/payments': 'Pagesat',
      '/admin/cash-settlement': 'Mandat Arkëtimi',
      '/admin/invoices': 'Faturat',
      '/admin/finance': 'Financat & Kontabiliteti',
      '/admin/daily-closing': 'Mbyllja Ditore',
      '/admin/backups': 'Menaxhimi i Backup',
      '/admin/shifts': 'Turnet e Shoferëve',
      '/admin/fleet': 'Menaxhim Flote',
      '/admin/passengers': 'Pasagjerët',
      '/admin/customers': 'Klientë të Shpeshtë',
      '/admin/routes': 'Rrugë të Paracaktuara',
      '/admin/analytics': 'Analitika',
      '/admin/logs': 'Audit Logs',
      '/admin/settings': 'Konfigurime'
    };
    
    // Handle dynamic routes
    if (location.pathname.includes('/drivers/') && location.pathname.includes('/profile')) {
      return 'Profili i Shoferit';
    }
    
    return titles[location.pathname] || 'Përmbledhje';
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(180deg, #f9f9f9 0%, #ececec 100%)' }}>
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'block' : 'hidden'} lg:block`}>
        <Sidebar 
          currentPath={location.pathname} 
          onNavigate={handleNavigate}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={handleToggleSidebarCollapse}
        />
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className={`content-area ${sidebarCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'} flex-1 flex flex-col`}>
        <Header 
          onMenuClick={() => setSidebarOpen(!sidebarOpen)} 
          title={getPageTitle()}
          onCreateBooking={handleCreateBooking}
        />
        <main className="p-6 flex-1">
          <Routes>
            <Route index element={<DashboardHome />} />
            <Route path="map" element={<MapPage />} />
            <Route path="rides" element={<RidesPage />} />
            <Route path="rides/operations" element={<RideOperationsPage basePath="/admin" />} />
            <Route path="rides/:rideId" element={<AdminRideDetailsPage />} />
            <Route path="drivers" element={<DriversPage />} />
            <Route path="drivers/:driverId/profile" element={<DriverProfilePage />} />
            <Route path="drivers/:driverId/edit" element={<EditDriverPage />} />
            <Route path="performance" element={<DriverPerformancePage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="cash-settlement" element={<CashSettlementPage />} />
            <Route path="shift-verification" element={<ShiftVerificationPage />} />
            <Route path="invoices" element={<InvoicesPage />} />
            <Route path="finance" element={<FinancePage />} />
            <Route path="daily-closing" element={<DailyClosingPage />} />
            <Route path="backups" element={<BackupsPage />} />
            <Route path="shifts" element={<DriverShiftsPage />} />
            <Route path="fleet" element={<FleetManagement />} />
            <Route path="fleet/:vehicleId/profile" element={<VehicleProfilePage />} />
            <Route path="passengers" element={<PassengersList />} />
            <Route path="calls" element={<CallLog />} />
            <Route path="routes" element={<RoutePresets />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="logs" element={<AuditLogsPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="push-notifications" element={<PushNotificationsPage />} />
          </Routes>
        </main>
        
        {/* Footer */}
        <footer className="lg:ml-0 py-4 px-6 border-t border-gray-200 bg-white/50 backdrop-blur-sm">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <p>© 2026 Admin Panel. Të gjitha të drejtat e rezervuara.</p>
            <div className="flex items-center gap-4">
              <span>Version 1.0.0</span>
              <span className="w-2 h-2 rounded-full bg-green-500" title="Sistemi aktiv"></span>
            </div>
          </div>
        </footer>
      </div>

      {/* Create Booking Modal */}
      <CreateBookingModal 
        isOpen={showBookingModal} 
        onClose={() => setShowBookingModal(false)} 
      />
    </div>
  );
};

export default DispatcherDashboard;
